/**
 * Router principal.
 *
 * Estructura:
 *   /login    entrada
 *   /         el escritorio del policlínico
 *   /me       el perfil de quien entró
 *
 * Sin `/:slug` y sin `/admin`: esta instalación es de un policlínico y no hay
 * plataforma por encima que administrar.
 */
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AppLayout } from "./layouts/AppLayout";
import {
  GuestOnlyRoute,
  ProtectedRoute,
  RequireFuncionalidad,
  RequirePermission,
} from "./components/ProtectedRoute";
import { TenantBrandingProvider } from "./auth/TenantBrandingProvider";
import { AgendaPage } from "./pages/AgendaPage";
import { AjustesPage } from "./pages/AjustesPage";
import { AtenderPacientePage } from "./pages/AtenderPacientePage";
import { AuditoriaPage } from "./pages/AuditoriaPage";
import { CajaPage } from "./pages/CajaPage";
import { ComprobanteDetailPage } from "./pages/ComprobanteDetailPage";
import { ComprobanteEmitirPage } from "./pages/ComprobanteEmitirPage";
import { ComprobantesListPage } from "./pages/ComprobantesListPage";
import { DashboardPage } from "./pages/DashboardPage";
import { GerenciaPage } from "./pages/GerenciaPage";
import { FarmaciaComprasPage } from "./pages/FarmaciaComprasPage";
import { FarmaciaDespachoPage } from "./pages/FarmaciaDespachoPage";
import { FarmaciaInventarioPage } from "./pages/FarmaciaInventarioPage";
import { FarmaciaKardexPage } from "./pages/FarmaciaKardexPage";
import { FarmaciaValorizacionPage } from "./pages/FarmaciaValorizacionPage";
import { LaboratorioPage } from "./pages/LaboratorioPage";
import { PortalAuthProvider } from "./auth/PortalAuthProvider";
import { PortalLayout } from "./pages/portal/PortalLayout";
import { PortalLoginPage } from "./pages/portal/PortalLoginPage";
import { PortalInicioPage } from "./pages/portal/PortalInicioPage";
import { PortalCitasPage } from "./pages/portal/PortalCitasPage";
import { PortalRecetasPage } from "./pages/portal/PortalRecetasPage";
import { PortalResultadosPage } from "./pages/portal/PortalResultadosPage";
import { PortalMisDatosPage } from "./pages/portal/PortalMisDatosPage";
import { EpisodioDetailPage } from "./pages/EpisodioDetailPage";
import { RecuperarPage } from "./pages/RecuperarPage";
import { LoginPage } from "./pages/LoginPage";
import { MePage } from "./pages/MePage";
import { CitaDetailPage } from "./pages/CitaDetailPage";
import { CitasPage } from "./pages/CitasPage";
import { MiEspacioPage } from "./pages/MiEspacioPage";
import { PacienteDetailPage } from "./pages/PacienteDetailPage";
import { PacientesListPage } from "./pages/PacientesListPage";
import { PagosPage } from "./pages/PagosPage";
import { PersonalListPage } from "./pages/PersonalListPage";
import { ProfesionalDetailPage } from "./pages/ProfesionalDetailPage";
import { RolesPage } from "./pages/RolesPage";
import { SedesListPage } from "./pages/SedesListPage";
import { SeriesPage } from "./pages/SeriesPage";
import { EspecialidadesPage } from "./pages/EspecialidadesPage";

/** Envoltorio que carga la marca del policlínico para todo lo de dentro. */
function BrandedSection() {
  return (
    <TenantBrandingProvider>
      <Outlet />
    </TenantBrandingProvider>
  );
}

/**
 * El árbol de rutas del policlínico, sin decir de dónde cuelga.
 *
 * Se declara aparte, en un fragmento, para que el árbol se lea de un vistazo
 * sin la sangría de la ruta padre. React Router entra en los fragmentos, así
 * que es exactamente lo mismo que escribirlo dentro.
 */
const rutasDelPoliclinico = (
  <>
          {/* Login branded para este tenant */}
          <Route element={<GuestOnlyRoute />}>
            <Route path="login" element={<LoginPage />} />
            {/* La misma ruta con y sin token: pedir el enlace y canjearlo son
                un solo trámite para quien lo hace. */}
            <Route path="recuperar" element={<RecuperarPage />} />
            <Route path="recuperar/:token" element={<RecuperarPage />} />
          </Route>

          {/* Portal del paciente. Cuelga del tenant para heredar su marca,
              pero fuera de ProtectedRoute y de AppLayout: tiene su propia
              sesión y su propio marco, sin nada del panel del personal. */}
          <Route
            path="portal"
            element={
              <PortalAuthProvider>
                <Outlet />
              </PortalAuthProvider>
            }
          >
            <Route path="entrar" element={<PortalLoginPage />} />
            <Route element={<PortalLayout />}>
              <Route index element={<PortalInicioPage />} />
              <Route path="citas" element={<PortalCitasPage />} />
              <Route path="recetas" element={<PortalRecetasPage />} />
              <Route path="resultados" element={<PortalResultadosPage />} />
              <Route path="mis-datos" element={<PortalMisDatosPage />} />
            </Route>
          </Route>

          {/* Rutas protegidas del tenant */}
          <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                {/* Sin permiso: todo usuario autenticado del tenant entra. */}
                <Route index element={<DashboardPage />} />
                {/* Gerencia: los números del negocio, para quien es socio. */}
                <Route element={<RequirePermission codigo="reporte:read" />}>
                  <Route path="gerencia" element={<GerenciaPage />} />
                </Route>
                <Route path="mi-espacio" element={<MiEspacioPage />} />
                <Route path="me" element={<MePage />} />
                {/* "Tarifas" se fusionó en Especialidades; mantenemos el link viejo vivo. */}
                <Route path="tarifas" element={<Navigate to="../especialidades" replace />} />

                {/* Con permiso. Los códigos son los mismos de nav-items.ts:
                    si cambias uno, cambia el otro o el menú mentirá. */}
                <Route element={<RequirePermission codigo="paciente:read" />}>
                  <Route path="pacientes" element={<PacientesListPage />} />
                  <Route path="pacientes/:id" element={<PacienteDetailPage />} />
                  <Route path="citas" element={<CitasPage />} />
                  <Route path="citas/:id" element={<CitaDetailPage />} />
                  <Route path="pacientes/:id/atender" element={<AtenderPacientePage />} />
                  <Route path="episodios/:id" element={<EpisodioDetailPage />} />
                </Route>

                {/* Agenda: admin, recepción y médico (el médico ve la suya).
                    Caja y contabilidad no tienen profesional:read. */}
                <Route element={<RequirePermission codigo="profesional:read" />}>
                  <Route path="agenda" element={<AgendaPage />} />
                </Route>

                {/* Farmacia. Despacho pide el permiso de dispensar y no el de
                    leer: mirar el inventario y entregar medicamentos son dos
                    responsabilidades distintas.

                    Todo el bloque cuelga del módulo: una clínica que no lo
                    contrató no tiene farmacia para nadie, ni para su
                    administrador. Ver core/funcionalidades.py. */}
                <Route element={<RequireFuncionalidad codigo="farmacia" />}>
                <Route element={<RequirePermission codigo="farmacia:dispensar" />}>
                  <Route path="farmacia/despacho" element={<FarmaciaDespachoPage />} />
                </Route>
                <Route element={<RequirePermission codigo="farmacia:read" />}>
                  <Route path="farmacia/inventario" element={<FarmaciaInventarioPage />} />
                  <Route path="farmacia/kardex" element={<FarmaciaKardexPage />} />
                  <Route
                    path="farmacia/valorizacion"
                    element={<FarmaciaValorizacionPage />}
                  />
                </Route>
                <Route element={<RequirePermission codigo="farmacia:compra" />}>
                  <Route path="farmacia/compras" element={<FarmaciaComprasPage />} />
                </Route>
                </Route>

                {/* Laboratorio: la mesa de quien procesa muestras. El médico
                    pide y lee sus órdenes desde la atención, no aquí. */}
                <Route element={<RequireFuncionalidad codigo="laboratorio" />}>
                  <Route element={<RequirePermission codigo="laboratorio:procesar" />}>
                    <Route path="laboratorio" element={<LaboratorioPage />} />
                  </Route>
                </Route>
                {/* Fichas del personal: solo quien puede ver a TODOS. El
                    backend además acota por profesional, no confiamos en esto. */}
                <Route element={<RequirePermission codigo="profesional:read_all" />}>
                  <Route path="personal" element={<PersonalListPage />} />
                  <Route path="profesionales/:id" element={<ProfesionalDetailPage />} />
                  {/* "Profesionales" se fusionó en "Personal"; los enlaces
                      viejos siguen funcionando. */}
                  <Route path="profesionales" element={<Navigate to="../personal" replace />} />
                </Route>

                <Route element={<RequirePermission codigo="tenant:read" />}>
                  <Route path="ajustes" element={<AjustesPage />} />
                </Route>
                <Route element={<RequirePermission codigo="rol:read" />}>
                  <Route path="roles" element={<RolesPage />} />
                </Route>
                <Route element={<RequirePermission codigo="sede:create" />}>
                  <Route path="sedes" element={<SedesListPage />} />
                </Route>
                <Route element={<RequirePermission codigo="especialidad:assign" />}>
                  <Route path="especialidades" element={<EspecialidadesPage />} />
                </Route>
                <Route element={<RequirePermission codigo="pago:read" />}>
                  <Route path="pagos" element={<PagosPage />} />
                </Route>
                <Route element={<RequirePermission codigo="pago:create" />}>
                  <Route path="caja" element={<CajaPage />} />
                </Route>
                <Route element={<RequireFuncionalidad codigo="facturacion_electronica" />}>
                  <Route element={<RequirePermission codigo="comprobante:read" />}>
                    <Route path="facturacion" element={<ComprobantesListPage />} />
                    <Route path="facturacion/emitir" element={<ComprobanteEmitirPage />} />
                    <Route path="facturacion/series" element={<SeriesPage />} />
                    <Route path="facturacion/:id" element={<ComprobanteDetailPage />} />
                  </Route>
                </Route>
                <Route element={<RequireFuncionalidad codigo="auditoria" />}>
                  <Route element={<RequirePermission codigo="auditoria:read" />}>
                    <Route path="auditoria" element={<AuditoriaPage />} />
                  </Route>
                </Route>
              </Route>
          </Route>
  </>
);

function App() {

  return (
    <BrowserRouter>
      <Routes>
        {/* Login de raíz. Va dentro de BrandedSection para que resuelva el
            branding: con un solo policlínico instalado, este login es el suyo
            y debe mostrar su logo y su nombre, no el de la plataforma. */}
        <Route element={<BrandedSection />}>
          <Route element={<GuestOnlyRoute />}>
            <Route path="/login" element={<LoginPage />} />
          </Route>
        </Route>

        {/* Todo cuelga de la raíz. No hay slug: la instalación es de un solo
            policlínico, así que la URL es `/citas`, no `/mi-salud/citas`. */}
        <Route path="/" element={<BrandedSection />}>
          {rutasDelPoliclinico}
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
